import React, {useEffect, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import find from 'lodash/find';
import {withRouter, type WithRouterProps} from 'react-router';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {DocSidebarPagePanels} from 'app/general/structures/DocSidebarPagePanels';
import StateButton from 'app/general/widgets/StateButton';
import {useLanesForDocument} from 'app/lanes/api';
import {deleteProcedureVersion} from 'app/procedures/actions';
import {useAccountSettingsState} from 'app/settings/api';
import {
  useSidebarIsOpenState,
  useToggleSidebarIsOpen,
} from 'app/settings/hooks';
import {
  type DashboardIsOpenPayloadType,
  VERSIONS_SIDEBAR_TOGGABLE,
} from 'app/settings/slice';
import {useModelChangeSubscription} from 'app/subscriptions';
import * as urls from 'app/urls';
import {reportUnexpectedState} from 'app/utils/errors';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {getPathFromURL} from 'app/utils/urls';
import {groupVersions} from 'app/version/utils';

import {
  GroupedVersionsItem,
  removeSelectedVersionGroupParam,
} from './GroupedVersionsItem';
import {VersionItem} from './VersionItem';

import type {LocationQuery} from './location-query';
import type {AbstractVersionType} from 'app/version/types';
import type {
  DetailPolicySerializedType,
  ProcedureHashidSerializedType,
  ProcessHashidSerializedType,
  QuizSerializedType,
} from 'sweet-types';

export const StyledListDIV = styled.div`
  .list-group {
    border-radius: 0;
  }

  .list-group-item {
    border-radius: 0;
    border-left: none;
    border-right: none;

    &.active {
      z-index: unset;
    }
  }
`;

type RouterProps = WithRouterProps<Record<string, never>, LocationQuery>;

interface Props<T extends AbstractVersionType> extends RouterProps {
  object:
    | ProcessHashidSerializedType
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType
    | QuizSerializedType;
  selectedVersion?: T;
  loadVersions: (objectId: number | string) => Promise<any>;
  loadMoreVersions: (objectId: number | string) => Promise<any>;
  moreToLoad: boolean;
  versions: Array<T>;
}

const VersionsSidebar = <T extends AbstractVersionType>({
  object,
  selectedVersion,
  moreToLoad,
  versions,
  loadVersions,
  loadMoreVersions,
  router,
  location,
}: Props<T>) => {
  const {query} = location;
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const toggleSidebarIsOpen = useToggleSidebarIsOpen();

  const sidebarProps: DashboardIsOpenPayloadType = {
    contentType: object.content_type,
    objectId: object.id,
    widgetName: VERSIONS_SIDEBAR_TOGGABLE,
  };
  const sidebarIsOpen = useSidebarIsOpenState(sidebarProps);
  const toggleSidebar = () => {
    toggleSidebarIsOpen(sidebarProps);
  };

  const {is_peruser, version_days, owner_email} =
    useAccountSettingsState().data!;
  const {is_owner} = useCurrentUserState().data!;

  const actions = useAsyncActions({deleteVersion: deleteProcedureVersion});

  const versionsIds = versions.map(({id}) => id).join(',');
  const lanesCt = lanesContentTypeMap[object.content_type];
  useLanesForDocument(
    lanesCt == null || versions.length == 0
      ? skipToken
      : {
          content_type: lanesCt,
          object_ids: versionsIds,
        }
  );

  useModelChangeSubscription(
    (message, {runWhenAllRelatedMatch}) => {
      runWhenAllRelatedMatch(
        message,
        `${object.content_type}version`,
        {
          content_type: object.content_type,
          id: object.id,
        },
        () => fetchData()
      );
    },
    [object.id, object.content_type]
  );

  const fetchData = whenVisible(() =>
    loadVersions(object.id).then(
      () => setIsLoading(false),
      () => setIsLoading(false)
    )
  );

  const isSelected = (versionId: number | string) => {
    return Boolean(
      (selectedVersion && versionId === (selectedVersion.id || 0)) ||
        (!selectedVersion && !versionId)
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  const upgradeActionWidget = () => (
    <p>
      {!is_owner ? (
        <a href={`mailto:${owner_email}`}>
          {gt('Contact your account owner to upgrade')}
        </a>
      ) : (
        <a href={urls.UPGRADE}>{gt('Upgrade now')}</a>
      )}
    </p>
  );

  const deleteVersion = (version: T) => {
    return actions.deleteVersion(version.url).then(() => {
      if (isSelected(version.id)) {
        const versionIndex = versions.map((v) => v.id).indexOf(version.id);
        const previousVersion =
          versions[versionIndex - 1] ||
          find(versions, (v) => v.id !== version.id);

        if (previousVersion != null) {
          router.replace({
            pathname: getPathFromURL(previousVersion.html_url),
            query: location.query,
          });
        } else {
          reportUnexpectedState('USEC5');
        }
      }
    });
  };

  const showMessage = !is_peruser && version_days > 0;
  function onClose() {
    router.replace(object.html_url);
  }
  return sidebarIsOpen ? (
    <DocSidebarPagePanels
      onClose={onClose}
      loading={isLoading}
      title={
        <span>
          {gt('Version History')}
          <button className="close d-none d-sm-inline" onClick={onClose}>
            <i className="fa fa-times" />
          </button>
          <button className="close d-sm-none" onClick={toggleSidebar}>
            <i className="fa fa-chevron-left" />
          </button>
        </span>
      }
      showCloseButton={false}
    >
      <StyledListDIV>
        {showMessage === true ? (
          <div className="m-3 small text-secondary font-italic">
            <p>
              {gt(
                'We provide %s days of version history for all your procedures and policies. If you would like up to 1 year history, request a change in your subscription to per user pricing',
                version_days
              )}
            </p>
            {upgradeActionWidget()}
          </div>
        ) : null}

        <ul className="list-group">
          {groupVersions(versions).map((group) => (
            <React.Fragment key={group[0].id}>
              {group.length === 1 ? (
                <VersionItem
                  canDelete={object.can_delete}
                  version={group[0]}
                  isSelected={isSelected(group[0].id)}
                  searchParams={removeSelectedVersionGroupParam(query)}
                  onDelete={() => deleteVersion(group[0])}
                />
              ) : (
                <GroupedVersionsItem
                  canDelete={object.can_delete}
                  versionGroup={group}
                  router={router}
                  location={location}
                  selectedVersion={selectedVersion ? selectedVersion.id : null}
                  deleteVersion={deleteVersion}
                />
              )}
            </React.Fragment>
          ))}
        </ul>

        {moreToLoad && (
          <div className="text-center my-2 mx-4 pb-2">
            <StateButton
              className="btn btn-secondary btn-sm btn-block"
              busyText={gt('Loading...')}
              onClick={() => loadMoreVersions(object.id)}
            >
              {gt('Load more')}
            </StateButton>
          </div>
        )}
      </StyledListDIV>
    </DocSidebarPagePanels>
  ) : null;
};

export function VersionsSidebarFactory<
  T extends AbstractVersionType
>(): React.ComponentType<Omit<Props<T>, keyof RouterProps>> {
  return withRouter<Props<T>>(VersionsSidebar);
}

/* types to be used by VersionsSidebar wrappers */
export type SpecificVersionsSidebarOwnProps = {
  object:
    | ProcessHashidSerializedType
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType
    | QuizSerializedType;
  selectedVersion: any;
};

export type SpecificVersionsSidebarStateProps<T extends AbstractVersionType> = {
  moreToLoad: boolean;
  versions: Array<T>;
  contentType: 'procedure' | 'policy' | 'process';
};

const lanesContentTypeMap = {
  procedure: 'procedureversion',
  process: 'processversion',
} as const;
